'use client';

import type {FC} from 'react';
import {useEffect} from 'react';
import clsx from 'clsx';

import {FadeInDiv} from '@/components/framer/fade-in-div';
import {Media} from '@/components/media';

import {useHero} from '../../context/page-head-context';
import type {HeroPageHead} from '../../types';

interface Props {
	hero: HeroPageHead;
}

export const BackgroundMedia: FC<Props> = ({hero}) => {
	const {media, mediaPosition, textColor, filter} = hero;

	const {setDarkHero} = useHero();

	useEffect(() => {
		if (mediaPosition === 'background' && textColor === 'light') {
			setDarkHero(true);
		}

		// Reset the context when the component unmounts
		return () => setDarkHero(false);
	}, [setDarkHero, textColor, mediaPosition]);

	if (mediaPosition !== 'background') {
		return null;
	}

	let filterOverlay: string[] = [];

	switch (filter) {
		case 'light-from-left':
			filterOverlay = [
				'w-3/4',
				'max-md:w-full',
				'h-full',
				'md:bg-gradient-to-r',
				'bg-[linear-gradient(0deg,#FEFAF080_0%,#FEFAF005_75%),linear-gradient(90deg,#FEFAF0_0%,#FEFAF000_75%)]',
				'from-beige-500',
				'to-transparent',
			];
			break;
		case 'dark-from-left':
			filterOverlay = [
				'w-3/4',
				'max-md:w-full',
				'h-full',
				'md:bg-gradient-to-r',
				'bg-[linear-gradient(0deg,#00000070_0%,#00000005_75%),linear-gradient(90deg,#00000070_0%,#00000000_75%)]',
				'from-black/60',
				'to-transparent',
			];
			break;
		case 'dark':
			filterOverlay = ['w-full', 'bg-black/20', 'h-full'];
			break;
		case 'light':
			filterOverlay = ['w-full', 'bg-beige-500/60', 'h-full'];
			break;
		case 'none':
			filterOverlay = [];
			break;
		default:
			filterOverlay = [
				'w-3/4',
				'max-md:w-full',
				'h-full',
				'md:bg-gradient-to-r',
				'bg-[linear-gradient(0deg,#FEFAF080_0%,#FEFAF005_75%),linear-gradient(90deg,#FEFAF0_0%,#FEFAF000_75%)]',
				'from-beige-500',
				'to-transparent',
			];
			break;
	}

	if (media?.image?.credit || media?.image?.description) {
		return (
			<FadeInDiv
				className={clsx(
					'absolute',
					'-top-28',
					'lg:-top-44',
					'w-screen',
					'left-[calc(50%-50vw)]',
					'bottom-0',
					'-z-10',
					'opacity-70',
				)}
			>
				<Media
					media={media}
					disabledControls={true}
					disabledMask={true}
				/>
				<div
					className={clsx(
						'text-sm',
						'mt-4',
						'flex',
						'flex-wrap',
						'justify-between',
						'gap-x-6',
						'gap-y-1',
						'absolute',
						'right-2',
						'left-2',
						'px-6',
						'mx-auto',
					)}
				>
					{media?.image?.description && (
						<div>{media.image.description}</div>
					)}
					{media?.image?.credit && (
						<div
							className={clsx(
								!media?.image?.description?.length && [
									'sm:justify-self-end',
									'sm:text-right',
									'w-full',
								],
							)}
						>
							{media.image.credit}
						</div>
					)}
				</div>
				<div
					className={clsx('absolute', 'top-0', filterOverlay, 'z-10')}
				/>
			</FadeInDiv>
		);
	}

	return (
		<FadeInDiv
			className={clsx(
				'absolute',
				'-top-28',
				'lg:-top-44',
				'w-screen',
				'left-[calc(50%-50vw)]',
				'bottom-0',
				'-z-10',
				'opacity-70',
			)}
		>
			<Media media={media} disabledControls={true} disabledMask={true} />
			<div className={clsx('absolute', 'top-0', filterOverlay, 'z-10')} />
		</FadeInDiv>
	);
};
